import { useEffect, useState } from "react";
import { Download, ExternalLink, Loader2, X } from "lucide-react";
import {
  downloadAttachment,
  openAttachmentInNewTab,
} from "@/api/taskAttachments";
import { isVideo } from "./constants";
import { useAttachmentBlob } from "./useAttachmentBlob";

/**
 * TASK-ATT polish (W12 view) — full attachment viewer modal.
 *
 * IMAGES (jpeg/png/webp — NOT svg/tiff) render as `<img object-contain>`.
 * COMP-S2b: mp4/mov play inline via `<video controls preload="metadata">` (the
 * blob is fetched once with `preload="metadata"` so a 60MB file does not stream
 * its whole body into memory before play). Both render from the SECURED download
 * URL fetched as a blob (token-header auth) → object URL, revoked on close/unmount
 * (no blob leak). Only ONE viewer is mounted at a time (caller state), so two big
 * videos never decode in parallel (mobile-OOM guard).
 *
 * Desktop ≈ 88vw × 88vh; mobile = fullscreen. Header: filename · Завантажити ·
 * Відкрити в новій вкладці · Close.
 *
 * PDF (D-146 W2): rendered inline via `<iframe src=objectUrl>` — the D-143
 * annex-preview precedent (AnnexWizardModal) codified the auth-blob→object-URL
 * →iframe path (browser PDF viewer, UA-sandboxed, PDF-JS disabled by default;
 * the blob is magic-byte-verified server-side). Before D-146 PDFs fell back to
 * «Відкрити в новій вкладці» on the row.
 */
export function AttachmentViewer({
  downloadUrl,
  filename,
  subtitle,
  mime,
  onClose,
}: {
  downloadUrl: string;
  filename: string;
  /** Dimmed suffix in the header (D-146: immutable original filename). */
  subtitle?: string;
  /** Video mime → `<video>`; application/pdf → iframe; else `<img>`. */
  mime?: string;
  onClose: () => void;
}) {
  const video = isVideo(mime);
  const pdf = mime === "application/pdf";
  // G6/П-14: viewer = EAGER — модалка видима за визначенням, IO-гейт тут
  // недоречний (fetch одразу, 0 тіків порожнечі понад мережевий час).
  const { src: objectUrl, error: loadError } = useAttachmentBlob(downloadUrl, {
    lazy: false,
    errorFallback: "Не вдалося відкрити прев'ю",
    // D-146 W2 fix: типізований blob — PDF-плагін Chrome вішається на
    // нескінченному лоадері, коли blob.type порожній/octet-stream.
    blobType: pdf ? "application/pdf" : undefined,
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const error = actionError ?? loadError;
  const [busy, setBusy] = useState(false);
  // D-146 W2 fix: watchdog вбудованого PDF — якщо iframe не відрапортував
  // load за 8с (плагін завис/заблокований у конкретному браузері), чесно
  // показуємо фолбек «нова вкладка / завантажити» замість вічного спінера.
  const [pdfFrame, setPdfFrame] = useState<"loading" | "ready" | "failed">("loading");
  useEffect(() => {
    if (!pdf || !objectUrl) return;
    setPdfFrame("loading");
    const t = window.setTimeout(
      () => setPdfFrame((s) => (s === "loading" ? "failed" : s)),
      8000,
    );
    return () => window.clearTimeout(t);
  }, [pdf, objectUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const runAction = (fn: () => Promise<unknown>) => {
    setBusy(true);
    void fn()
      .catch((e: unknown) =>
        setActionError(e instanceof Error ? e.message : "Дія не вдалася"),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-0 sm:p-6"
      onClick={onClose}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Перегляд ${filename}`}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-hidden rounded-none bg-background shadow-2xl sm:h-[88vh] sm:w-[88vw] sm:rounded-lg"
      >
        <div className="flex items-center gap-2 border-b px-3 py-2 sm:px-4 sm:py-3">
          <span
            className="min-w-0 flex-1 truncate text-sm font-medium"
            title={subtitle ? `${filename} · файл: ${subtitle}` : filename}
          >
            {filename}
            {subtitle && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">· файл: {subtitle}</span>
            )}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => runAction(() => downloadAttachment(downloadUrl, filename))}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Завантажити</span>
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => runAction(() => openAttachmentInNewTab(downloadUrl))}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Відкрити в новій вкладці</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          className={
            pdf && objectUrl && !error
              ? // PDF: НЕ items-center — iframe отримує визначений бокс через
                // relative+inset (h-full під align-center — крос-браузерно крихкий).
                "relative flex-1 overflow-hidden bg-muted/20"
              : "flex flex-1 items-center justify-center overflow-auto bg-muted/20 p-2 sm:p-4"
          }
        >
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : objectUrl ? (
            pdf ? (
              pdfFrame === "failed" ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Вбудований перегляд PDF не завантажився у цьому браузері.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        runAction(async () => {
                          const opened = await openAttachmentInNewTab(downloadUrl);
                          if (!opened) await downloadAttachment(downloadUrl, filename);
                        })
                      }
                      className="rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
                    >
                      Відкрити в новій вкладці
                    </button>
                    <button
                      type="button"
                      onClick={() => runAction(() => downloadAttachment(downloadUrl, filename))}
                      className="rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent"
                    >
                      Завантажити
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <iframe
                    src={objectUrl}
                    title={`Перегляд ${filename}`}
                    onLoad={() => setPdfFrame("ready")}
                    className="absolute inset-0 h-full w-full border-0 bg-card"
                  />
                  {pdfFrame === "loading" && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </>
              )
            ) : video ? (
              <video
                src={objectUrl}
                controls
                preload="metadata"
                className="max-h-full max-w-full"
              >
                {filename}
              </video>
            ) : (
              <img
                src={objectUrl}
                alt={filename}
                className="max-h-full max-w-full object-contain"
              />
            )
          ) : (
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>
    </div>
  );
}
